'use client';

import { useState } from 'react';
import { useInsufficientCreditsToast } from '@/new_components/alerts/use-insufficient-credits-toast';
import { Bell, BellRing, Bookmark, BookmarkCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/use-session';
import { useCompanyWatch } from '@/hooks/use-company-watch';
import { useWatchlist } from '@/hooks/use-watchlist';
import { Button } from '@/components/ui/button';
import {
  type CompanyAlertDialog,
  CompanyAlertDialogs,
} from './CompanyAlertDialogs';

/**
 * The bookmark, plus — once the company is saved — a bell for updates.
 *
 * Saving is free; subscribing to updates costs credits per day. So the
 * bookmark only saves, and right after a save the subscribe prompt asks
 * whether the user also wants updates, with the price. The bell answers the
 * same question later: outlined while the company is only saved (opens the
 * same prompt), filled once subscribed (opens the full alert wizard — the
 * one a watched list uses, since a company and a list are one subscription
 * kind).
 *
 * The bell only appears once saved: subscribing to an unsaved company would
 * also save it, and two buttons that both mean "save" is a worse first
 * impression than one.
 */
export function WatchlistButton({
  euId,
  className,
}: {
  euId: string;
  className?: string;
}) {
  const { data: session } = useSession();
  const { t } = useTranslation();
  const { isWatched, toggle, isMutating } = useWatchlist(euId);
  const { watch } = useCompanyWatch(euId);
  const showInsufficient = useInsufficientCreditsToast();
  const [dialog, setDialog] = useState<CompanyAlertDialog>(null);

  if (!session) return null;

  const subscribed = isWatched && !!watch?.enabled;
  const label = isWatched
    ? t('watchlist.remove', 'Remove from Watchlist')
    : t('watchlist.add', 'Add to Watchlist');
  const bellLabel = subscribed
    ? t('watchlist.configureAlerts', 'Configure alerts')
    : t('watchlist.subscribe', 'Subscribe to updates');

  const handleClick = async () => {
    const action = await toggle();
    if (action === 'added') {
      setDialog('subscribe');
    } else if (action === 'removed') {
      toast.success(t('watchlist.removed', 'Removed from Watchlist'), {
        position: 'top-center',
      });
    } else if (action === 'credits') {
      showInsufficient();
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={isMutating}
        className={cn(
          'h-10 w-10 text-foreground hover:text-foreground',
          className,
        )}
        aria-pressed={isWatched}
        aria-label={label}
        title={label}
      >
        {isWatched ? (
          <BookmarkCheck className="size-6 fill-current text-foreground" />
        ) : (
          <Bookmark className="size-6 text-foreground" />
        )}
      </Button>

      {isWatched && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setDialog(subscribed ? 'wizard' : 'subscribe')}
          className={cn(
            'h-10 w-10 hover:text-foreground',
            subscribed ? 'text-foreground' : 'text-muted-foreground',
          )}
          aria-pressed={subscribed}
          aria-label={bellLabel}
          title={bellLabel}
        >
          {subscribed ? (
            <BellRing className="size-5 fill-current" />
          ) : (
            <Bell className="size-5" />
          )}
        </Button>
      )}

      <CompanyAlertDialogs euId={euId} open={dialog} onOpenChange={setDialog} />
    </>
  );
}
